/**
 * 網址 ↔ 視圖的對照表（#01：重整回原點、連結傳不出去）＋四區座標系（T01）。
 *
 * 刻意**不引入 router 套件**：整個面板只有「一級分區／系統分頁／目前對話」
 * 三個維度，用 hash + 一張表就夠，也不必改 Vite／Worker 的 fallback。
 *
 * 用 hash（`#/...`）而非 pathname 的原因：
 * - 靜態托管（Pages／Worker assets）不需要 SPA fallback 把 /inbox/x 改寫回 index.html；
 * - 直接用 `<a href="#/inbox/xx">` 就能分享，貼進任何 IM 都不會被吃掉。
 *
 * ## 四區（一級）與視圖（二級）
 *
 * | 一級 | 正式網址 | Section | 舊網址（全部仍可解析，解析後網址會被正規化） |
 * |:--|:--|:--|:--|
 * | 工作台 | `#/inbox`、`#/inbox/<id>` | `workspace` | 同左（`section` 舊名 `conversations`） |
 * | 身分 | `#/identities` | `identities` | `#/accounts`、`#/accounts/sessions`、`#/accounts/creds`、`#/admin/accounts`、`#/pool` |
 * | 房間 | `#/rooms`、`#/sg` | `rooms`、`sg` | `#/rooms`、`#/sg` |
 * | 系統 | `#/system/<tab>` | `system` | `#/admin/<tab>`、`#/health`、`#/ai`、`#/ai-observability` |
 *
 * `tab` ＝ `overview｜sessions｜monitor｜rooms-map｜params｜grants｜invites｜ai`。解析失敗（亂打的網址／舊書籤）一律回預設頁，
 * 不拋錯、不擋登入——但**不會留死連結**：每一條舊網址都有落點。
 *
 * 身分（#08）：網址只是「想去哪」，能不能去由 `canAccessSection()` 決定。
 * 這一支刻意留在路由層（純函式、不碰 React），選單與網址守門吃同一份名單，
 * 才不會出現「選單藏起來、貼網址還是進得去」的假分權。
 */
import type { AdminTab } from '@chat-rea/console-ui';
import { FUNNEL_PLATFORMS } from '../lib/funnel';
import type { Section } from './ConsoleShell';

/**
 * 系統區的三段（T04）。`ops`「任務與系統」已併進 `sessions`（工作階段）：
 * 執行紀錄是「某個工作階段的一次執行」，跟連線中的工作階段同一組主鍵。
 * 型別直接沿用 `AdminTab`，外殼與網址不會各留一份 union 而慢慢走鐘。
 */
export type SystemTab = AdminTab;

export interface Route {
  section: Section;
  systemTab: SystemTab;
  /** 工作台 `#/inbox/<id>` 與瀑布 `#/waterfall/<id>`。`null` 是該區清單。 */
  sessionId: string | null;
  /** 瀑布 `#/waterfall?platform=`。其他分頁維持 null。 */
  platform: string | null;
}

export const DEFAULT_ROUTE: Route = {
  section: 'workspace',
  systemTab: 'overview',
  sessionId: null,
  platform: null,
};

/** 系統區真正的分頁（白名單：亂打 `#/system/bogus` 落回總覽）。 */
export const SYSTEM_TABS: readonly SystemTab[] = [
  'overview',
  'sessions',
  'monitor',
  'rooms-map',
  'params',
  'grants',
  'invites',
  'ai',
];

/**
 * 已下線的分頁 → 現在的落點。`ops`（任務與系統）在 T04 併進「工作階段」，
 * 舊書籤與舊側欄連結不能變成 404 或空白頁。
 */
const RETIRED_SYSTEM_TABS: Readonly<Record<string, SystemTab>> = { ops: 'sessions' };

function decode(part: string): string {
  try {
    return decodeURIComponent(part);
  } catch {
    // 半截的 %E4… 之類：原樣當 id，總比整頁炸掉好（找不到就回清單）。
    return part;
  }
}

/** `#/inbox/abc` → ['inbox', 'abc']；忽略 query 與空段。 */
function segments(hash: string): string[] {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const [path] = raw.split('?');
  return (path ?? '').split('/').filter(Boolean).map(decode);
}

function waterfallPlatform(hash: string): string | null {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  const query = raw.split('?')[1] ?? '';
  const value = new URLSearchParams(query).get('platform');
  if (value && (FUNNEL_PLATFORMS as readonly string[]).includes(value)) return value;
  return null;
}

function systemRoute(second: string | undefined): Route {
  const raw = second ?? '';
  const tab: SystemTab = SYSTEM_TABS.includes(raw as SystemTab)
    ? (raw as SystemTab)
    : (RETIRED_SYSTEM_TABS[raw] ?? 'overview');
  return { ...DEFAULT_ROUTE, section: 'system', systemTab: tab };
}

export function parseRoute(hash: string): Route {
  const [head, second] = segments(hash);
  switch (head) {
    // ── 工作台 ────────────────────────────────────────────────
    case 'inbox':
      return { ...DEFAULT_ROUTE, section: 'workspace', sessionId: second ?? null };
    case 'waterfall':
      return {
        ...DEFAULT_ROUTE,
        section: 'waterfall',
        sessionId: second ?? null,
        platform: waterfallPlatform(hash),
      };

    // ── 身分（單一入口；舊分頁與舊管理分頁一律落到同一頁）─────
    case 'identities':
    case 'accounts': // `#/accounts/sessions|creds` 的第二段直接忽略
    case 'pool': // 舊 id 直打（有人把 section 名當網址）
      return { ...DEFAULT_ROUTE, section: 'identities' };

    // ── 房間 ──────────────────────────────────────────────────
    case 'rooms':
      return { ...DEFAULT_ROUTE, section: 'rooms' };
    case 'sg':
      // sugargirl 代聊：房間區的第二個視圖，深連結保留（`#/sg` 是對外貼慣的網址）。
      return { ...DEFAULT_ROUTE, section: 'sg' };

    // ── 系統 ──────────────────────────────────────────────────
    case 'system':
      return systemRoute(second);
    case 'admin':
      // 舊書籤 `#/admin/accounts`（管理›帳號池已併進身分頁）→ 身分，不留分頁。
      if (second === 'accounts') return { ...DEFAULT_ROUTE, section: 'identities' };
      return systemRoute(second);
    case 'health':
      // 舊健康頁已併進系統 › 工作階段（A3）。
      return { ...DEFAULT_ROUTE, section: 'system', systemTab: 'sessions' };
    case 'ai-observability':
    case 'ai':
      // AI 成效 → 系統第四段（`#/system/ai`）。
      return { ...DEFAULT_ROUTE, section: 'system', systemTab: 'ai' };

    default:
      return DEFAULT_ROUTE;
  }
}

export function routeToHash(route: Route): string {
  switch (route.section) {
    case 'waterfall': {
      const path = route.sessionId ? `/waterfall/${encodeURIComponent(route.sessionId)}` : '/waterfall';
      return route.platform ? `${path}?platform=${encodeURIComponent(route.platform)}` : path;
    }
    case 'identities':
      return '/identities';
    case 'rooms':
      return '/rooms';
    case 'sg':
      return '/sg';
    case 'system':
      return `/system/${route.systemTab}`;
    default:
      // 工作台與瀑布可以帶目前對話；其餘分頁沒有第二段。
      return route.sessionId ? `/inbox/${encodeURIComponent(route.sessionId)}` : '/inbox';
  }
}

/** 目前網址的完整形式（含 `#`），給比對／寫入 history 用。 */
export function routeToUrl(route: Route): string {
  return `#${routeToHash(route)}`;
}

/**
 * 「房間」與「系統」兩區只有管理員能去（#08）。
 *
 * **不在名單裡的就是聊手也能去的**：`workspace`（工作台）與 `identities`（身分／帳號）。
 * 名單刻意寫在這裡、而不是散在選單與畫面裡，因為它同時決定「側欄給不給」與
 * 「直接打網址給不給」——兩者只要有一邊漏掉，身分就只是裝飾。
 */
const ADMIN_ONLY_SECTIONS: readonly Section[] = ['rooms', 'sg'];

export type AccessFlags = {
  canAdmin: boolean;
  canEngineer: boolean;
};

/** 這個身分能不能去這一區。聊手：工作台／瀑布／身分；工程師另開系統；房間仍僅 admin。 */
export function canAccessSection(section: Section, access: boolean | AccessFlags): boolean {
  const flags: AccessFlags =
    typeof access === 'boolean' ? { canAdmin: access, canEngineer: access } : access;
  if (flags.canAdmin) return true;
  if (ADMIN_ONLY_SECTIONS.includes(section)) return false;
  if (section === 'system') return flags.canEngineer || flags.canAdmin;
  return true;
}

/** 系統分頁細權限。邀請／聊手授權僅 admin。 */
export function canAccessSystemTab(tab: SystemTab, access: AccessFlags): boolean {
  if (access.canAdmin) return true;
  if (!access.canEngineer) return false;
  return tab !== 'grants' && tab !== 'invites';
}

/** 被擋下來時要導回的地方：聊手的家＝工作台。 */
export const DENIED_FALLBACK: Route = { ...DEFAULT_ROUTE, section: 'workspace' };
